/**
 * Goals Page  —  /prioritization/strategy/goals
 *
 * Diagram position (from the strategy relationship map):
 *   Objective → [can be specified by] → Goal → [can anchor to] → Prioritization Items
 *   Desired Outcome → [measured by] → Goal
 *   OST solution nodes → [link to] → Goal
 *
 * This page manages Goals independently of Objectives.
 * Goals are metrics — measurable signals that tell you an Objective is being achieved.
 * Upstream:   Objective → Strategy (shown as context)
 * Downstream: Prioritization Items (via methodology_connections)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { btnPrimary } from '@/components/mono/primitives';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Target,
  Flag,
  Plus,
  Pencil,
  Trash2,
  TrendingUp,
  Clock,
  Layers,
  X,
  Check,
  Link2 as LinkIcon,
  TreeDeciduous,
  Search,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { StrategyConnectivityHelpButton } from './StrategyConnectivityDocModal';
import {
  listGoals,
  listObjectives,
  createGoal,
  updateGoal,
  deleteGoal,
  type Goal,
  type StrategicObjective,
} from '../../../lib/strategyAlignment';
import { useStrategyStore } from '../../../stores/prioritization/strategyStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { sendStrategyObjectChanges } from '../../../lib/strategyObjectLinks';
import { LinkedElsewhere } from './LinkedElsewhere';

// ============================================================================
// Types
// ============================================================================

interface Strategy { id: string; name: string; description?: string; }

// ============================================================================
// Badge helpers (local copy)
// ============================================================================

const PRIORITY_COLORS: Record<string, { bg: string; text: string }> = {
  critical: { bg: 'bg-[var(--coral-dim)]',    text: 'text-[var(--coral)]' },
  high:     { bg: 'bg-[var(--mustard-dim)]', text: 'text-[var(--mustard)]' },
  medium:   { bg: 'bg-[var(--slate-dim)]',   text: 'text-[var(--text-sec)]' },
  low:      { bg: 'bg-[var(--hover-tint)]',  text: 'text-[var(--text-sec)]' },
};
const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  active:    { bg: 'bg-[var(--green-dim)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]' },
  achieved:  { bg: 'bg-[var(--slate-dim)]',  text: 'text-[var(--text-sec)]' },
  deferred:  { bg: 'bg-[var(--mustard-dim)]',   text: 'text-[var(--mustard)]' },
  cancelled: { bg: 'bg-[var(--hover-tint)]',   text: 'text-[var(--text-ter)]' },
};
const HORIZON_ICONS: Record<string, typeof Clock> = {
  quarterly:    Clock,
  annual:       TrendingUp,
  'multi-year': Layers,
};
function PriorityBadge({ value }: { value: string }) {
  const c = PRIORITY_COLORS[value] ?? { bg: 'bg-[var(--hover-tint)]', text: 'text-[var(--text-sec)]' };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.bg} ${c.text} capitalize`}>{value}</span>;
}
function StatusBadge({ value }: { value: string }) {
  const c = STATUS_COLORS[value] ?? { bg: 'bg-[var(--hover-tint)]', text: 'text-[var(--text-ter)]' };
  return <span className={`text-xs px-2 py-0.5 rounded-full ${c.bg} ${c.text} capitalize`}>{value}</span>;
}
function HorizonBadge({ value }: { value: string }) {
  const Icon = HORIZON_ICONS[value] ?? Clock;
  return <span className="flex items-center gap-1 text-xs text-[var(--text-ter)]"><Icon className="h-3 w-3" />{value}</span>;
}

// ============================================================================
// Form primitives (local)
// ============================================================================

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
        {label}{required && <span className="text-[var(--coral)] ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}
const INPUT_CLS  = 'w-full px-3 py-2 text-sm border border-[var(--rule)]  focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 bg-[var(--bg-raised)] text-[var(--text-pri)] placeholder-gray-400 dark:placeholder-gray-500';
const SELECT_CLS = `${INPUT_CLS} appearance-none pr-8`;
const CHEVRON = (
  <svg className="h-4 w-4 pointer-events-none" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
  </svg>
);
function Sel({ value, onChange, children, disabled }: { value: string; onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void; children: React.ReactNode; disabled?: boolean }) {
  return (
    <div className="relative">
      <select value={value} onChange={onChange} disabled={disabled} className={SELECT_CLS}>{children}</select>
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[var(--text-ter)]">{CHEVRON}</span>
    </div>
  );
}
function DeleteButton({ onConfirm }: { onConfirm: () => void }) {
  const [confirming, setConfirming] = useState(false);
  if (confirming) return (
    <span className="flex items-center gap-1">
      <button type="button" onClick={() => { onConfirm(); setConfirming(false); }} className="p-1 rounded text-[var(--coral)] hover:bg-[var(--coral-dim)]" title="Confirm"><Check className="h-3.5 w-3.5" /></button>
      <button type="button" onClick={() => setConfirming(false)} className="p-1 rounded text-[var(--text-ter)] hover:bg-[var(--hover-tint)]" title="Cancel"><X className="h-3.5 w-3.5" /></button>
    </span>
  );
  return <button type="button" onClick={() => setConfirming(true)} className="p-1 rounded text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>;
}
function Modal({ title, subtitle, onClose, onSave, saveLabel, saving, canSave, error, children }: {
  title: string; subtitle?: string; onClose: () => void; onSave: () => void;
  saveLabel?: string; saving?: boolean; canSave?: boolean; error?: string | null;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)] max-w-lg w-full overflow-hidden">
          <div className="flex items-start justify-between px-6 py-4 border-b border-[var(--rule)]">
            <div>
              <h3 className="text-base font-semibold text-[var(--text-pri)]">{title}</h3>
              {subtitle && <p className="text-xs text-[var(--text-ter)] mt-0.5">{subtitle}</p>}
            </div>
            <button type="button" onClick={onClose} className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]"><X className="h-4 w-4" /></button>
          </div>
          <div className="px-6 py-5 space-y-4">
            {error && <div className="border border-[var(--coral)] dark:border-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 px-3 py-2 text-sm text-[var(--coral)] dark:text-[var(--coral)]">{error}</div>}
            {children}
          </div>
          <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--rule)] bg-[var(--surface)]/50 dark:bg-[var(--bg-raised)]/30">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors">Cancel</button>
            <button type="button" onClick={onSave} disabled={saving || canSave === false} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? 'Saving…' : (saveLabel ?? 'Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Goal modal
// ============================================================================

type GoalForm = {
  name: string; description: string;
  objectiveId: string;
  strategyId: string;
  timeHorizon: 'quarterly' | 'annual' | 'multi-year';
  priority: 'critical' | 'high' | 'medium' | 'low';
  status: 'active' | 'achieved' | 'deferred' | 'cancelled';
  metricName: string;
  targetValue: string;
  currentValue: string;
  unit: string;
};

function GoalModal({ initial, strategies, objectives, defaultObjectiveId, onClose, onSaved, workspaceId, userId }: {
  initial?: Goal; strategies: Strategy[]; objectives: StrategicObjective[];
  defaultObjectiveId?: string; onClose: () => void; onSaved: () => void;
  workspaceId: string; userId: string;
}) {
  const [form, setForm] = useState<GoalForm>(() => {
    const objId = initial?.objectiveId ?? defaultObjectiveId ?? '';
    const obj   = objectives.find((o) => o.id === objId);
    return {
      name:         initial?.name         ?? '',
      description:  initial?.description  ?? '',
      objectiveId:  objId,
      strategyId:   initial?.strategyId ?? obj?.strategyId ?? '',
      timeHorizon:  initial?.timeHorizon ?? 'annual',
      priority:     initial?.priority    ?? 'high',
      status:       initial?.status      ?? 'active',
      metricName:   initial?.metricName   ?? '',
      targetValue:  initial?.targetValue  ?? '',
      currentValue: initial?.currentValue ?? '',
      unit:         initial?.unit         ?? '',
    };
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState<string | null>(null);

  const handleObjectiveChange = (objId: string) => {
    const obj = objectives.find((o) => o.id === objId);
    setForm((f) => ({ ...f, objectiveId: objId, strategyId: obj?.strategyId ?? f.strategyId }));
  };

  const filteredObjectives = form.strategyId ? objectives.filter((o) => o.strategyId === form.strategyId) : objectives;
  const derivedStrategy    = strategies.find((s) => s.id === form.strategyId);

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError(null);
    try {
      const metricFields = {
        metricName:   form.metricName.trim()   || null,
        targetValue:  form.targetValue.trim()  || null,
        currentValue: form.currentValue.trim() || null,
        unit:         form.unit.trim()         || null,
      };
      if (initial) {
        await updateGoal(initial.id, { name: form.name.trim(), description: form.description.trim(), objectiveId: form.objectiveId || undefined, strategyId: form.strategyId || undefined, timeHorizon: form.timeHorizon, priority: form.priority, status: form.status, ...metricFields });
        // Linked to a record elsewhere: send the change now, not at the next 15-minute run.
        void sendStrategyObjectChanges(workspaceId, 'goal', initial.id).catch(() => undefined);
      } else {
        await createGoal({ name: form.name.trim(), description: form.description.trim(), objectiveId: form.objectiveId || undefined, strategyId: form.strategyId || undefined, timeHorizon: form.timeHorizon, priority: form.priority, status: form.status, ...metricFields, workspaceId, createdBy: userId }, userId);
      }
      onSaved();
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={initial ? 'Edit Goal' : 'New Goal'} subtitle="Goals are metrics — measurable signals that tell you an Objective is being achieved." onClose={onClose} onSave={handleSave} saving={saving} canSave={!!form.name.trim()} error={error} saveLabel={initial ? 'Save Changes' : 'Create Goal'}>
      <Field label="Name" required>
        <input autoFocus type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Monthly active users reach 10,000" className={INPUT_CLS} />
      </Field>
      <Field label="Description">
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Context for this goal" className={`${INPUT_CLS} resize-none`} />
      </Field>

      {/* Metric fields */}
      <div className="border border-[var(--green)] dark:border-[var(--green)] bg-[var(--green-dim)] dark:bg-[var(--green)]/20 p-3 space-y-3">
        <p className="text-xs font-semibold text-[var(--green)] dark:text-[var(--text-ter)] uppercase tracking-wide">Metric</p>
        <Field label="What is being measured">
          <input type="text" value={form.metricName} onChange={(e) => setForm({ ...form, metricName: e.target.value })} placeholder="e.g., Monthly active users" className={INPUT_CLS} />
        </Field>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Target value">
            <input type="text" value={form.targetValue} onChange={(e) => setForm({ ...form, targetValue: e.target.value })} placeholder="e.g., 10000" className={INPUT_CLS} />
          </Field>
          <Field label="Current value">
            <input type="text" value={form.currentValue} onChange={(e) => setForm({ ...form, currentValue: e.target.value })} placeholder="e.g., 6200" className={INPUT_CLS} />
          </Field>
          <Field label="Unit">
            <input type="text" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="e.g., users, %, $K" className={INPUT_CLS} />
          </Field>
        </div>
      </div>

      {/* ↑ Upstream: Strategy (optionally filter objectives by strategy first) */}
      <Field label="Strategy">
        <Sel
          value={form.strategyId}
          onChange={(e) => {
            const newStratId = e.target.value;
            const objStillValid = objectives.find((o) => o.id === form.objectiveId && o.strategyId === newStratId);
            setForm((f) => ({ ...f, strategyId: newStratId, objectiveId: objStillValid ? f.objectiveId : '' }));
          }}
        >
          <option value="">{strategies.length === 0 ? '— No strategies yet —' : '— Any strategy —'}</option>
          {strategies.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Sel>
        {strategies.length === 0 && (
          <p className="mt-1 text-xs text-[var(--text-ter)]">
            Create a strategy first at <Link to="/prioritization/strategy" className="underline hover:text-primary-600">Strategies</Link>.
          </p>
        )}
      </Field>

      {/* ↑ Upstream: Objective */}
      <Field label="Objective">
        <div className="relative">
          <select value={form.objectiveId} onChange={(e) => handleObjectiveChange(e.target.value)} className={SELECT_CLS}>
            <option value="">{filteredObjectives.length === 0 ? '— No objectives for this strategy —' : '— Select an objective —'}</option>
            {filteredObjectives.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[var(--text-ter)]">{CHEVRON}</span>
        </div>
        {derivedStrategy && form.objectiveId && (
          <p className="mt-1 text-xs text-[var(--text-ter)] flex items-center gap-1">
            <Target className="h-3 w-3" /> Strategy: {derivedStrategy.name}
          </p>
        )}
      </Field>

      <div className="grid grid-cols-3 gap-3">
        <Field label="Time horizon">
          <Sel value={form.timeHorizon} onChange={(e) => setForm({ ...form, timeHorizon: e.target.value as GoalForm['timeHorizon'] })}>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
            <option value="multi-year">Multi-year</option>
          </Sel>
        </Field>
        <Field label="Priority">
          <Sel value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value as GoalForm['priority'] })}>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </Sel>
        </Field>
        <Field label="Status">
          <Sel value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as GoalForm['status'] })}>
            <option value="active">Active</option>
            <option value="achieved">Achieved</option>
            <option value="deferred">Deferred</option>
            <option value="cancelled">Cancelled</option>
          </Sel>
        </Field>
      </div>
      {initial && (
        <div className="border-t border-[var(--rule)] pt-3">
          <LinkedElsewhere
            workspaceId={workspaceId}
            type="goal"
            id={initial.id}
            values={{ name: initial.name, description: initial.description ?? '' }}
          />
        </div>
      )}
    </Modal>
  );
}

// ============================================================================
// Goals list
// ============================================================================

export default function GoalsPage() {
  const userId             = useUserStore((s) => s.userId) ?? '';
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId) ?? '';

  const strategyMap     = useStrategyStore((s) => s.strategies);
  const fetchStrategies = useStrategyStore((s) => s.fetchFromSupabase);
  const strategies      = useMemo<Strategy[]>(
    () => Object.values(strategyMap).map((s) => ({ id: s.id, name: s.name, description: s.northStarGoal ?? undefined })),
    [strategyMap],
  );

  const [searchParams]     = useSearchParams();
  const defaultObjectiveId = searchParams.get('objectiveId') ?? '';
  const defaultStrategyId  = searchParams.get('strategyId') ?? '';

  const [filterStrategyId,  setFilterStrategyId]  = useState(defaultStrategyId);
  const [goalSearch, setGoalSearch] = useState('');
  const [objectives,  setObjectives]  = useState<StrategicObjective[]>([]);
  const [goals,       setGoals]       = useState<Goal[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [modal,       setModal]       = useState<'create' | Goal | null>(null);

  const loadAll = useCallback(async () => {
    if (!currentWorkspaceId) return;
    const [o, g] = await Promise.all([
      listObjectives(currentWorkspaceId),
      listGoals(currentWorkspaceId),
    ]);
    setObjectives(o);
    setGoals(g);
  }, [currentWorkspaceId]);

  useEffect(() => {
    if (!currentWorkspaceId) { setLoading(false); return; }
    setLoading(true);
    Promise.all([fetchStrategies(), loadAll()]).finally(() => setLoading(false));
  }, [currentWorkspaceId, loadAll, fetchStrategies]);

  const visible = useMemo(() => {
    let list = filterStrategyId
      ? goals.filter((g) => g.strategyId === filterStrategyId)
      : goals;
    const q = goalSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (g) =>
          g.name.toLowerCase().includes(q) ||
          (g.description?.toLowerCase().includes(q)) ||
          (g.metricName?.toLowerCase().includes(q))
      );
    }
    return list;
  }, [goals, filterStrategyId, goalSearch]);

  const hasFilters = Boolean(filterStrategyId || goalSearch.trim());

  if (!currentWorkspaceId) return <div className="p-8 text-center text-[var(--text-ter)] border border-[var(--rule)] bg-[var(--surface)]">Select a workspace to manage goals.</div>;
  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>;

  const filterName = strategies.find((s) => s.id === filterStrategyId)?.name;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Flag className="h-4 w-4 text-[var(--mustard)]" />}
        iconBg="bg-[var(--mustard-dim)]"
        title="Goals"
        subtitle={filterName ? `Filtered to strategy: ${filterName}` : 'Metrics — measurable signals that an Objective is being achieved. Goals can anchor Prioritization Items.'}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <StrategyConnectivityHelpButton context="goals" />
            {hasFilters && (
              <button
                type="button"
                onClick={() => {
                  setFilterStrategyId('');
                  setGoalSearch('');
                }}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
              >
                <X className="h-3.5 w-3.5" /> Clear filters
              </button>
            )}
            <Link to="/prioritization/strategy/objectives" className="flex items-center gap-1.5 px-3 py-2 text-sm text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]">
              <Target className="h-3.5 w-3.5" /> Objectives
            </Link>
            <Link to="/prioritization/strategy" className="flex items-center gap-1.5 px-3 py-2 text-sm text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]">
              <TreeDeciduous className="h-3.5 w-3.5" /> Strategies
            </Link>
            <button type="button" onClick={() => setModal('create')} {...btnPrimary()}>
              <Plus className="h-4 w-4" /> New Goal
            </button>
          </div>
        }
      />

      {/* Strategy dropdown + search — avoids N strategy pills; search helps large goal lists */}
      {(strategies.length > 0 || goals.length > 0) && (
        <div className="border border-[var(--rule)] bg-[var(--surface)]/80 dark:bg-[var(--bg)]/40 px-4 py-4 space-y-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            {strategies.length > 0 ? (
              <div className="flex-1 min-w-0">
                <label htmlFor="goals-filter-strategy" className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5">
                  Strategy
                </label>
                <div className="relative">
                  <select
                    id="goals-filter-strategy"
                    value={filterStrategyId}
                    onChange={(e) => setFilterStrategyId(e.target.value)}
                    className={`${SELECT_CLS} w-full lg:max-w-md`}
                  >
                    <option value="">
                      All strategies ({goals.length} {goals.length === 1 ? 'goal' : 'goals'})
                    </option>
                    {strategies.map((s) => {
                      const c = goals.filter((g) => g.strategyId === s.id).length;
                      return (
                        <option key={s.id} value={s.id}>
                          {s.name} ({c} {c === 1 ? 'goal' : 'goals'})
                        </option>
                      );
                    })}
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[var(--text-ter)]">{CHEVRON}</span>
                </div>
                <p className="mt-1.5 text-xs text-[var(--text-ter)]">
                  {strategies.length} {strategies.length === 1 ? 'strategy' : 'strategies'} in this workspace — pick one to focus, or leave on &ldquo;All strategies&rdquo;.
                </p>
              </div>
            ) : (
              <div className="flex-1 min-w-0 lg:max-w-md">
                <p className="text-xs text-[var(--text-ter)]">
                  No classic strategies in this workspace yet. You can still search and manage goals below.
                </p>
              </div>
            )}
            <div className={`flex-1 min-w-0 ${strategies.length === 0 ? 'w-full' : ''}`}>
              <label htmlFor="goals-search" className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5">
                Search goals
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-ter)]" />
                <input
                  id="goals-search"
                  type="search"
                  value={goalSearch}
                  onChange={(e) => setGoalSearch(e.target.value)}
                  placeholder="Name, description, or metric…"
                  autoComplete="off"
                  className={`${INPUT_CLS} pl-9 w-full ${strategies.length > 0 ? 'lg:max-w-md' : ''}`}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {goals.length === 0 ? (
        <div className="text-center py-16 text-[var(--text-ter)]">
          <Flag className="h-12 w-12 mx-auto text-[var(--text-ter)] mb-3" />
          <p className="font-medium text-[var(--text-sec)]">No goals yet</p>
          <p className="text-sm mt-1 max-w-sm mx-auto">"Improve retention" is an Objective. "Churn &lt; 2% by Q4" is a Goal. Goals provide the measurable definition of done for an Objective.</p>
          <button type="button" onClick={() => setModal('create')} {...btnPrimary("mt-4")}>
            <Plus className="h-4 w-4" /> New Goal
          </button>
        </div>
      ) : visible.length === 0 ? (
        <div className="border border-dashed border-[var(--rule)] bg-[var(--surface)]/50 dark:bg-[var(--bg)]/30 px-6 py-12 text-center">
          <Search className="h-10 w-10 mx-auto text-[var(--text-ter)] mb-3" />
          <p className="font-medium text-[var(--text-sec)]">No goals match</p>
          <p className="text-sm text-[var(--text-ter)] mt-1 max-w-md mx-auto">
            {hasFilters
              ? 'Try clearing the strategy filter or search. Goals may exist outside this view.'
              : 'Adjust your filters to see goals.'}
          </p>
          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setFilterStrategyId('');
                setGoalSearch('');
              }}
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-primary-700 dark:text-primary-400 border border-primary-200 dark:border-primary-800 hover:bg-primary-50 dark:hover:bg-primary-900/20"
            >
              <X className="h-3.5 w-3.5" /> Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          {(filterStrategyId || goalSearch.trim()) && (
            <p className="text-xs text-[var(--text-ter)]">
              Showing <span className="font-medium text-[var(--text-sec)]">{visible.length}</span> of{' '}
              {goals.length} goal{goals.length === 1 ? '' : 's'}
              {filterName ? (
                <>
                  {' '}
                  · strategy: <span className="font-medium text-[var(--text-sec)]">{filterName}</span>
                </>
              ) : null}
              {goalSearch.trim() ? (
                <>
                  {' '}
                  · search: <span className="font-medium text-[var(--text-sec)]">&ldquo;{goalSearch.trim()}&rdquo;</span>
                </>
              ) : null}
            </p>
          )}
        <div className="border border-[var(--rule)] overflow-hidden bg-[var(--bg-raised)] shadow-sm divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
          {visible.map((g) => {
            const objective = objectives.find((o) => o.id === g.objectiveId);
            const strategy  = strategies.find((s) => s.id === (objective?.strategyId ?? g.strategyId));

            return (
              <div key={g.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5 group hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/60 transition-colors">
                <Flag className="h-4 w-4 text-[var(--mustard)] flex-shrink-0" />
                {/* Grows to fill the row; on a phone the metric and badges wrap below it */}
                <div className="min-w-0 flex-[1_1_12rem]">
                  <p className="text-sm font-semibold text-[var(--text-pri)] truncate">{g.name}</p>
                  {g.description && <p className="text-xs text-[var(--text-ter)] truncate mt-0.5">{g.description}</p>}
                  {/* ↑ Upstream context: Objective → Strategy */}
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {objective ? (
                      <Link to={`/prioritization/strategy/objectives?strategyId=${objective.strategyId ?? ''}`} className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 px-1.5 py-0.5 rounded hover:bg-primary-100 transition-colors">
                        <Target className="h-2.5 w-2.5" />{objective.name}
                      </Link>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] italic">
                        <LinkIcon className="h-2.5 w-2.5" />Not linked to an objective
                      </span>
                    )}
                    {strategy && (
                      <Link to={`/prioritization/strategy/${strategy.id}`} className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] bg-[var(--surface)] px-1.5 py-0.5 rounded hover:text-primary-600 transition-colors">
                        <Target className="h-2.5 w-2.5 opacity-50" />{strategy.name}
                      </Link>
                    )}
                  </div>
                </div>
                {/* Metric display */}
                {g.metricName && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[var(--green-dim)] dark:bg-[var(--green)]/30 border border-[var(--green)] dark:border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)] text-xs max-w-full flex-wrap">
                    <span className="font-medium">{g.metricName}</span>
                    {g.currentValue && <span className="opacity-60">{g.currentValue}{g.unit ? ` ${g.unit}` : ''}</span>}
                    {g.targetValue && <span className="opacity-40">→</span>}
                    {g.targetValue && <span className="font-semibold">{g.targetValue}{g.unit ? ` ${g.unit}` : ''}</span>}
                  </div>
                )}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <PriorityBadge value={g.priority} />
                  <StatusBadge value={g.status} />
                  <HorizonBadge value={g.timeHorizon} />
                  <button type="button" onClick={() => setModal(g)} className="p-1 rounded text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] opacity-0 group-hover:opacity-100 transition-all" title="Edit">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <DeleteButton onConfirm={async () => { await deleteGoal(g.id); loadAll(); }} />
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {(modal === 'create' || (modal && typeof modal === 'object')) && (
        <GoalModal
          initial={modal !== 'create' ? (modal as Goal) : undefined}
          strategies={strategies}
          objectives={objectives}
          defaultObjectiveId={modal === 'create' ? defaultObjectiveId : undefined}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); loadAll(); }}
          workspaceId={currentWorkspaceId}
          userId={userId}
        />
      )}
    </div>
  );
}
